import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { Org, modelClass as orgModelClass } from '/imports/api/orgs';
import { modelClass as userModelClass } from '/imports/api/users';
import { Account, modelClass as accountModelClass } from '/imports/api/accounts';
import { Message } from '/imports/api/messages';

export interface Conversation extends Doc {
  _id: string;
  name?: string;
  orgIds?: string[];
  accountIds?: string[];
  userIds?: string[];
  orderId?: string;
  refundId?: string;
  lastMessage?: Message;
  lastMessageAt?: Date;
}
export type Model = Conversation;

export const Conversations = new Mongo.Collection<Conversation>('conversations');

type Refs = References<Model, {
  orgIds: Org;
  accountIds: Account;
  userIds: Meteor.User;
}>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Conversations,
  editableFields: ['name'],
  listMethod: DeclareMethod<ListMethodArg<Conversation>, ListMethodResponse<Conversation, Refs>>('listConversations'),
  getMethod: DeclareMethod<string, GetMethodResponse<Conversation, Refs>>('getConversation'),
  createMethod: DeclareMethod<Partial<Conversation>, string>('createConversation'),
  updateMethod: DeclareMethod<Partial<Conversation>, string>('updateConversation'),
  deleteMethod: DeclareMethod<string>('deleteConversation'),
  pathPrefix: '/conversations',
  asString: (document) => document.name || document._id,
  modelName: strings.Conversation(),
  pluralName: strings.Conversations(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (conversation: Partial<Conversation>) => {
    const errors: { [key: string]: string[] } = {};
    validateString(errors, conversation, 'name');
    return errors;
  },
  references: {
    orgIds: { modelClass: orgModelClass, fields: { name: 1 } },
    userIds: { modelClass: userModelClass, fields: { username: 1 } },
    accountIds: { modelClass: accountModelClass, fields: { firstName: 1, lastName: 1 } },
  },
};

export const getConversationForOrg = DeclareMethod<string, GetMethodResponse<Conversation, Refs>>('getConversationForOrg');

Meteor.isServer && Meteor.startup(async () => {
  await Conversations.createIndexAsync({ orgIds: 1 }, { sparse: true });
  await Conversations.createIndexAsync({ accountIds: 1 }, { sparse: true });
  await Conversations.createIndexAsync({ userIds: 1 }, { sparse: true });
  await Conversations.createIndexAsync({ orderId: 1 }, { sparse: true, unique: true });
  await Conversations.createIndexAsync({ refundId: 1 }, { sparse: true, unique: true });
});
